import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plane, Loader2, Search, IndianRupee } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function flightMargin(flight) {
  const sell = Number(flight.price || flight.totalAmount || 0);
  const net = Number(flight.netFare || flight.supplierNet || flight.net_fare || 0);
  if (net > 0 && sell >= net) return sell - net;
  const markup = Number(flight.markup || flight.markup_amount || 0);
  return markup > 0 ? markup : null;
}

export default function StaffSearchResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const from = params.get('from') || '';
  const to = params.get('to') || '';
  const date = params.get('date') || '';
  const adults = params.get('adults') || '1';

  const runSearch = useCallback(async () => {
    if (!from || !to || !date) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/search-all`, {
        params: {
          from, to, date, adults,
          user_type: 'b2c',
        },
      });
      const list = [
        ...(res.data?.onward || res.data?.flights || []),
        ...(res.data?.return || []),
      ];
      setFlights(list);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [from, to, date, adults]);

  useEffect(() => { runSearch(); }, [runSearch]);

  const handleBook = (flight) => {
    sessionStorage.setItem('staff_booking_draft', JSON.stringify({
      flightData: flight,
      searchParams: { from, to, date, adults },
    }));
    navigate('/staff/review');
  };

  const handleQuickSearch = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const q = new URLSearchParams({
      from: fd.get('from'),
      to: fd.get('to'),
      date: fd.get('date'),
      adults: fd.get('adults') || '1',
    });
    navigate(`/staff/search?${q.toString()}`);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="staff-search">
      <h1 className="text-2xl font-bold text-[#1B375B] mb-4">Flight Search</h1>

      <form onSubmit={handleQuickSearch} className="bg-white rounded-xl border p-4 mb-6 grid grid-cols-2 md:grid-cols-5 gap-3">
        <input name="from" defaultValue={from} placeholder="From (BOM)" className="px-3 py-2 border rounded-lg text-sm uppercase" required />
        <input name="to" defaultValue={to} placeholder="To (DEL)" className="px-3 py-2 border rounded-lg text-sm uppercase" required />
        <input name="date" type="date" defaultValue={date} className="px-3 py-2 border rounded-lg text-sm" required />
        <input name="adults" type="number" min="1" defaultValue={adults} className="px-3 py-2 border rounded-lg text-sm" />
        <button type="submit" className="bg-[#1B375B] text-white rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2">
          <Search className="w-4 h-4" /> Search
        </button>
      </form>

      {error && <p className="text-rose-600 text-sm mb-4">{error}</p>}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#659A9B]" /></div>
      ) : (
        <div className="space-y-3">
          {flights.map((f, i) => {
            const margin = flightMargin(f);
            return (
              <div key={f.priceId || f.id || i} className="bg-white rounded-xl border p-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Plane className="w-5 h-5 text-[#659A9B]" />
                  <div>
                    <p className="font-bold text-[#1B375B]">
                      {f.from || f.origin} → {f.to || f.destination}
                      <span className="text-gray-400 font-normal ml-2">{f.airline || f.airline_code}</span>
                    </p>
                    <p className="text-xs text-gray-500">{f.departureTime || f.departure} · {f.source || 'tripjack'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-lg font-bold text-[#1B375B]">₹{Number(f.price || 0).toLocaleString('en-IN')}</p>
                    {margin != null && (
                      <p className="text-xs text-emerald-700 font-semibold flex items-center justify-end gap-1">
                        <IndianRupee className="w-3 h-3" /> Margin ₹{margin.toLocaleString('en-IN')}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleBook(f)}
                    className="px-4 py-2 bg-[#659A9B] text-white rounded-lg text-sm font-semibold"
                    data-testid="staff-select-flight"
                  >
                    Book
                  </button>
                </div>
              </div>
            );
          })}
          {!loading && from && to && flights.length === 0 && (
            <p className="text-center text-gray-500 py-12">No flights found</p>
          )}
          {!from && !to && (
            <p className="text-center text-gray-500 py-12">Enter route and date to search</p>
          )}
        </div>
      )}
    </div>
  );
}
